import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Building, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { verifyTeacherSchoolId } from '../../../services/teacherService';

// Add type definitions for the API response
interface SchoolData {
  id: string;
  name: string;
  status: string;
  paymentStatus: string;
}

interface TeacherData {
  id: string;
  teacherId: string;
  status: string;
}

interface TeacherVerificationResponse {
  success: boolean;
  data: {
    message: string;
    data: {
      school: SchoolData;
      teacher: TeacherData;
    };
  };
}

// Styled Components
const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
`;

const CardHeader = styled.div`
  padding: 2rem 2rem 1rem;
`;

const CardTitle = styled.h2`
  color: #6d28d9;
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
`;

const CardSubtitle = styled.p`
  color: #4b5563;
  font-size: 1rem;
  margin-top: 0.5rem;
`;

const FormSpace = styled.div`
  padding: 0 2rem 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const ErrorText = styled.p`
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
`;

const InfoBox = styled.div`
  padding: 1rem;
  background-color: #f5f3ff;
  border: 1px solid #e9d5ff;
  border-radius: 0.5rem;
  margin: 1rem 0 1.5rem;
`;

const PaymentAlertBox = styled.div`
  padding: 1rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  margin: 1rem 0 1.5rem;
`;

const PaymentAlertContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const PaymentAlertTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #dc2626;
  margin: 0 0 0.25rem;
`;

const PaymentAlertText = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
`;

const InfoContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const InfoTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6d28d9;
  margin: 0 0 0.25rem;
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: #8b5cf6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #7c3aed;
  }

  &:disabled {
    background-color: #c4b5fd;
    cursor: not-allowed;
  }
`;

// Component Props Interface
interface SchoolDetailsProps {
  formData: {
    schoolName: string;
    schoolId: string;
    teacherId: string;
    email?: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onContinue: () => void;
  loading?: boolean;
}

const SchoolDetails: React.FC<SchoolDetailsProps> = ({
  formData,
  onInputChange,
  onContinue,
  loading = false
}) => {
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<{isPaid: boolean; message: string} | null>(null);

  // Store school name in localStorage when component mounts or school name changes
  useEffect(() => {
    if (formData.schoolName) {
      localStorage.setItem('schoolName', formData.schoolName);
    }
  }, [formData.schoolName]);

  const handleVerifyAccount = async () => {
    if (!formData.teacherId) {
      setVerificationError('Please enter a Teacher ID Code provided by your school admin ');
      return;
    }

    if (!formData.schoolName) {
      setVerificationError('School name is required for verification');
      return;
    }

    setVerifying(true);
    setVerificationError(null);
    setPaymentStatus(null);

    try {
      const response = await verifyTeacherSchoolId(formData.teacherId, formData.schoolName);
      console.log('Verification Response:', response);
      
      if (response.success) {
        console.log('School Payment Status:', response.data.data.school.paymentStatus);
        
        if (response.data.data.school.paymentStatus === 'unpaid' || response.data.data.school.paymentStatus === 'pending') {
          setPaymentStatus({
            isPaid: false,
            message: 'Your school has not completed the subscription payment. Please contact your school administrator to complete the payment process.'
          });
          toast.error('School subscription payment pending');
          return;
        }

        toast.success(`Welcome! Teacher ID verified successfully.`);
        onContinue();
      } else {
        setVerificationError(response.data.message || 'Invalid Teacher ID Code');
        toast.error('Invalid Teacher ID Code');
      }
    } catch (error: any) {
      console.error('Verification Error:', error);
      
      // Extract error message from the response
      const errorMessage = error.response?.data?.message || error.message || '';
      console.log('Error Message:', errorMessage);

      // Check for specific error cases
      if (errorMessage.includes('not paid') || 
          errorMessage.includes('payment') ||
          errorMessage.includes('subscription') ||
          errorMessage.includes('School not found or not paid')) {
        setPaymentStatus({
          isPaid: false,
          message: 'Your school has not completed the subscription payment. Please contact your school administrator to complete the payment process.'
        });
        toast.error('School subscription payment pending');
      } else if (errorMessage.includes('not found')) {
        setVerificationError('School not found. Please check the school name and try again.');
        toast.error('School not found');
      } else if (errorMessage.includes('Internal server error')) {
        // Handle internal server error specifically
        setPaymentStatus({
          isPaid: false,
          message: 'Your school has not completed the subscription payment. Please contact your school administrator to complete the payment process.'
        });
        toast.error('School subscription payment pending');
      } else {
        // Show the actual error message from the backend
        setVerificationError(errorMessage || 'Failed to verify Teacher ID. Please try again.');
        toast.error(errorMessage || 'Verification failed');
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>School Information</CardTitle>
        <CardSubtitle>Enter your school details to connect your account</CardSubtitle>
      </CardHeader>

      <FormSpace>
        {paymentStatus && !paymentStatus.isPaid && (
          <PaymentAlertBox>
            <PaymentAlertContent>
              <AlertCircle size={20} color="#dc2626" />
              <div>
                <PaymentAlertTitle>Payment Required</PaymentAlertTitle>
                <PaymentAlertText>{paymentStatus.message}</PaymentAlertText>
              </div>
            </PaymentAlertContent>
          </PaymentAlertBox>
        )}

        {verificationError && !paymentStatus && (
          <PaymentAlertBox>
            <PaymentAlertContent>
              <AlertCircle size={20} color="#dc2626" />
              <div>
                <PaymentAlertTitle>Verification Error</PaymentAlertTitle>
                <PaymentAlertText>{verificationError}</PaymentAlertText>
              </div>
            </PaymentAlertContent>
          </PaymentAlertBox>
        )}

        <FormGroup>
          <Label htmlFor="schoolName">School Name</Label>
          <InputGroup>
            <InputIcon>
              <Building size={18} color="#9ca3af" />
            </InputIcon>
            <Input
              type="text"
              name="schoolName"
              id="schoolName"
              value={formData.schoolName}
              onChange={onInputChange}
              placeholder="Enter your school name"
              required
            />
          </InputGroup>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="schoolId">School ID Code</Label>
          <InputGroup>
            <InputIcon>
              <Lock size={18} color="#9ca3af" />
            </InputIcon>
            <Input
              type="text"
              name="schoolId"
              id="schoolId"
              value={formData.schoolId}
              onChange={(e) => {
                setVerificationError(null);
                setPaymentStatus(null);
                onInputChange(e);
              }}
              placeholder="Enter your school ID code"
              required
            />
          </InputGroup>
          <HelpText>Enter the ID code provided by your school administrator</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="teacherId">Teacher ID Code</Label>
          <InputGroup>
            <InputIcon>
              <Lock size={18} color="#9ca3af" />
            </InputIcon>
            <Input
              type="text"
              name="teacherId"
              id="teacherId"
              value={formData.teacherId}
              onChange={(e) => {
                setVerificationError(null);
                setPaymentStatus(null);
                onInputChange(e);
              }}
              placeholder="Enter your teacher ID code"
              required
            />
          </InputGroup>
          <HelpText>Enter your teacher ID code (e.g., POI-25-01)</HelpText>
        </FormGroup>

        <Button 
          onClick={handleVerifyAccount} 
          disabled={verifying || loading || !formData.schoolId || !formData.schoolName || !formData.teacherId}
        >
          {verifying ? 'Verifying...' : 'Continue'}
        </Button>
      </FormSpace>
    </Card>
  );
};

export default SchoolDetails;